import { useSearchParams } from "react-router-dom";

import create from "zustand";
import { LayerSearchFormValues } from "../types";
import { Geometry } from "geojson";

export const useSearchForm = (): [
  LayerSearchFormValues,
  (
    layer: number | "",
    query: string | undefined,
    attributes: string[] | undefined
  ) => void
] => {
  const [params, setParams] = useSearchParams();
  const layer = parseInt(params.get("layer") || "0") || "";
  const query = params.get("query") || "";
  const attributes = (params.get("attributes") || "")
    .split(",")
    .filter((attr) => !!attr);

  function updateParams(
    layer: number | "",
    query: string | undefined,
    attributes: string[] | undefined
  ) {
    const updated = new URLSearchParams(params);
    if (layer) updated.set("layer", layer.toString());
    else updated.delete("layer");

    if (query) updated.set("query", query);
    else updated.delete("query");

    if (attributes) updated.set("attributes", attributes.join(","));
    else updated.delete("attributes");

    setParams(updated);
  }

  return [{ layer, query, attributes }, updateParams];
};

export const useSearchSelected = (): [
  { selected: string; geometry: Geometry | null },
  (selected: string | undefined, geometry: Geometry | null) => void
] => {
  const [params, setParams] = useSearchParams();
  const [geometry, setGeometry] = useStore((state) => [
    state.featureGeometry,
    state.setFeatureGeometry,
  ]);
  const selected = params.get("selected") || "";

  function updateParams(
    selected: string | undefined,
    geometry: Geometry | null
  ) {
    const newParams = new URLSearchParams(params);
    if (selected) newParams.set("selected", selected);
    else newParams.delete("selected");

    setGeometry(geometry);
    setParams(newParams);
  }

  return [{ selected, geometry }, updateParams];
};

type StoreState = {
  featureGeometry: Geometry | null;
  setFeatureGeometry: (geometry: Geometry | null) => void;
};

const useStore = create<StoreState>((set) => ({
  featureGeometry: null,
  setFeatureGeometry: (geometry: Geometry | null) =>
    set({ featureGeometry: geometry }),
}));
